import { action, makeObservable, observable, runInAction } from 'mobx';
import { subscribe, eventBus } from 'mobx-event-bus2';

import RootStore from 'stores/Root/Root';
import API from 'utils/api';
import Logger from 'utils/logger';
import { EventType } from 'utils/events/constants';

export interface KnockGuidesConfig {
  publicApiKey: string;
  userId: string;
  token: string;
  guideChannelId: string;
}

export default class KnockGuides {
  store: RootStore;

  api: typeof API;

  @observable config?: KnockGuidesConfig;

  @observable initialised = false;

  private loading = false;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;

    eventBus.register(this);
  }

  @action.bound
  async loadConfig(): Promise<void> {
    if (this.initialised || this.loading) {
      return;
    }

    this.loading = true;
    try {
      const { data } = await this.api.user.getKnockToken()();

      if (data.enabled && data.token && data.publicApiKey && data.guideChannelId && data.userId) {
        runInAction(() => {
          this.config = {
            publicApiKey: data.publicApiKey as string,
            userId: data.userId as string,
            token: data.token as string,
            guideChannelId: data.guideChannelId as string,
          };
        });
      }
    } catch (e) {
      Logger.warn('Failed to load Knock guides config', e);
    } finally {
      runInAction(() => {
        this.initialised = true;
        this.loading = false;
      });
    }
  }

  fetchToken = async (): Promise<string> => {
    try {
      const { data } = await this.api.user.getKnockToken({ background: true })();
      return data.token ?? '';
    } catch (e) {
      Logger.warn('Failed to refresh Knock guides token', e);
      return '';
    }
  };

  @subscribe(EventType.LoggedOut)
  @action
  logout(): void {
    this.config = undefined;
    this.initialised = false;
    this.loading = false;
  }
}
